/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {chunk, noop} from '@benchling/js-utils';
import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import React from 'react';

import TagValueEditor from 'metadata/TagValueEditor';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import * as ComputedTagUtil from 'schemas/tags/computed/ComputedTagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import InfoIcon from 'ui-library/icons/InfoIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {convertUTCDatetimeToLocal} from 'util/DateUtil';

import EditableSchemaTag, {ValidationInfo} from './EditableSchemaTag';

interface Props {
  autoFocus: boolean;
  className?: string;
  fileId?: string;
  itemLinkAllowed: boolean;
  isCreating: boolean;
  isWritable: boolean;
  labelClassName?: string;
  labelStyle?: any; // TODO: PropTypes.oneOf(Object.values(FieldLabel.STYLES))
  onUpdateTags: (...args: Array<any>) => any;
  sequenceParts?: Array<any>;
  tags?: Array<any>; // TODO: FileConstants.TAG_SHAPE
  tagValidationInfos?: {[id: string]: ValidationInfo | null};
  tagPopovers?: {[id: string]: string | null};
  // When not creating, wait for confirmation from this function before entering edit mode
  confirmBeforeStartEditing?: () => Promise<boolean>;
  // Disable and render provided tooltip if non-null
  disableEditsWithTooltip?: string | null;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function adaptTags(tags: Array<any>) {
  return tags.map((tag) =>
    tag.tagSchemaField.fieldType === FIELD_TYPES.DATETIME.type
      ? {
          ...tag,
          value: convertUTCDatetimeToLocal(tag.value),
        }
      : tag
  );
}

/**
 * Render tag inputs in width=6 Bootstrap columns.
 */
class SchemaTagInputs extends React.Component<Props> {
  static defaultProps = {
    autoFocus: false,
    itemLinkAllowed: true,
    isCreating: false,
    isWritable: true,
  };

  _handleUpdateAllTags = (tagIndex: any, newValue: any): void => {
    // For the `isCreating` case, all callers expect a full list of tags on every update, rather than just
    // the single tag being edited.
    const updatedTags = reactUpdate(this.props.tags, {[tagIndex]: {$merge: newValue}});
    this.props.onUpdateTags(updatedTags);
  };

  _handleUpdateTag = (tagIndex: any, newValue: any): void => {
    this.props.onUpdateTags({[tagIndex]: newValue});
  };

  override render(): JSX.Element | null {
    const columns: any = [];
    let displayIndex = 0;

    const adaptedTags = adaptTags(this.props.tags || []);
    adaptedTags.forEach((tag, tagIndex) => {
      if (!ComputedTagUtil.isComputedTag(tag)) {
        // use tagPopovers to override the tooltipText stored on the tag schema
        const tagPopover = this.props.tagPopovers?.[tag.tagSchemaField.systemName];
        const tagValidationInfo = this.props.tagValidationInfos?.[tag.tagSchemaField.systemName];
        const editor = (
          <TagValueEditor
            autoFocus={this.props.autoFocus && displayIndex === 0}
            fileId={this.props.fileId}
            itemLinkAllowed={this.props.itemLinkAllowed}
            onChange={(newValue: any) => {
              this._handleUpdateAllTags(tagIndex, newValue);
            }}
            onKeyDown={noop}
            schemaFieldSelectorId={tag.tagSchemaField.schemaFieldSelectorId}
            sequenceParts={this.props.sequenceParts}
            tag={tag}
          />
        );
        const tooltipText = tagPopover || tag.tagSchemaField.tooltipText;
        columns.push(
          <Col key={displayIndex} xs={6}>
            {this.props.isCreating ? (
              <div
                {...getTestProps({
                  component: 'SchemaTagInput',
                  element: 'root',
                  key: tag.tagSchemaField.name,
                })}
              >
                <FieldLabel
                  className={this.props.labelClassName}
                  label={tag.tagSchemaField.displayName}
                  labelStyle={this.props.labelStyle}
                  isRequired={tag.tagSchemaField.isRequired}
                />
                {tooltipText && (
                  <span className="u-mini-spacing-left">
                    <Popover
                      renderContent={() => <span>{tooltipText}</span>}
                      placement={FloatingPlacement.TOP}
                      interaction={FloatingInteraction.HOVER}
                    >
                      <InfoIcon />
                    </Popover>
                  </span>
                )}
                <div className="u-position-relative u-flex-grow">{editor}</div>
              </div>
            ) : (
              <EditableSchemaTag
                fileId={this.props.fileId}
                itemLinkAllowed={this.props.itemLinkAllowed}
                isWritable={this.props.isWritable}
                labelClassName={this.props.labelClassName}
                labelStyle={this.props.labelStyle}
                tag={tag}
                tagPopover={tagPopover}
                tagValidationInfo={tagValidationInfo}
                onUpdateTag={(newValue) => {
                  this._handleUpdateTag(tagIndex, newValue);
                }}
                schemaFieldSelectorId={tag.tagSchemaField.schemaFieldSelectorId}
                sequenceParts={this.props.sequenceParts}
                confirmBeforeStartEditing={this.props.confirmBeforeStartEditing}
                disableEditsWithTooltip={this.props.disableEditsWithTooltip}
              />
            )}
          </Col>
        );
        displayIndex++;
      }
    });

    if (columns.length === 0) {
      return null;
    }

    const rows = Array.from(chunk(columns, 2));

    return (
      <Row className={this.props.className}>
        {rows.map((cols, idx) => (
          <div
            className={classNames(
              // Parent of Bootstrap Cols needs to have a clearfix class. See https://getbootstrap.com/docs/3.4/css/#helper-classes-clearfix
              'clearfix',
              rows.length - 1 === idx ? 'u-small-spacing-bottom' : 'u-moderate-spacing-bottom'
            )}
            key={`clearfix div ${idx}`}
          >
            {cols}
          </div>
        ))}
      </Row>
    );
  }
}

export default SchemaTagInputs;
